import { Checkbox, CheckboxProps, Flex } from "antd";
import { FC, useCallback, useMemo } from "react";
import { Point, SubtaskFacings, TaskAssortmentMatrix } from "tm-shared/types";

import { ForceStepComplete } from "@/features/ForceStepComplete";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { setAssortmentMatrixSubtaskPayload } from "@/store/slices/progress";
import { setMdTasksConfig } from "@/store/slices/ui";

import {
    getNextSubtaskId,
    makeForceStepCompletePayload,
} from "../../AssortmentMatrix.utils";

interface FacingsControllerProps {
    subtask: SubtaskFacings;
    point: Point;
    task: TaskAssortmentMatrix;
}

export const FacingsController: FC<FacingsControllerProps> = ({
    subtask,
    point,
    task,
}) => {
    const dispatch = useAppDispatch();

    const pointId = point.id;
    const taskId = task.id;
    const subtaskId = subtask.subtaskId;

    const tasksConfig = useAppSelector((state) => state.ui.mdTasksConfig);

    // Конфігурація поточної підзадачі
    const currentSubtaskConfig = useMemo(
        () => tasksConfig?.[pointId]?.[taskId]?.subtasks?.[subtaskId],
        [tasksConfig, pointId, taskId, subtaskId],
    );

    const shouldShowUncompleted = useMemo(
        () => currentSubtaskConfig?.showUncompleted ?? false,
        [currentSubtaskConfig?.showUncompleted],
    );

    const onChange: CheckboxProps["onChange"] = (e) => {
        dispatch(
            setMdTasksConfig({
                pointId,
                taskId,
                subtaskId,
                field: "showUncompleted",
                value: e.target.checked,
            }),
        );

        dispatch(
            setMdTasksConfig({
                pointId,
                taskId,
                subtaskId,
                value: null,
            }),
        );
    };

    const handleForceStepComplete = useCallback(
        async (comment: string) => {
            const payload = makeForceStepCompletePayload({
                subtask,
                comment,
            });

            if (!payload) return;

            dispatch(
                setAssortmentMatrixSubtaskPayload({
                    taskId,
                    subtaskId,
                    payload,
                }),
            );

            const { nextSubtaskId } = getNextSubtaskId({
                subtasks: task.context.subtasks,
                currentSubtaskId: subtaskId,
            });

            if (nextSubtaskId) {
                dispatch(
                    setMdTasksConfig({
                        pointId,
                        taskId,
                        field: "activeItem",
                        value: nextSubtaskId,
                    }),
                );
            }
        },
        [dispatch, pointId, subtask, subtaskId, task.context.subtasks, taskId],
    );

    return (
        <Flex gap={16} align="center" justify="space-between">
            <Checkbox onChange={onChange} checked={shouldShowUncompleted}>
                Показати невиконані
            </Checkbox>

            <ForceStepComplete
                onComplete={handleForceStepComplete}
                taskName={task.name}
                pointName={point.name}
                entity="step"
            />
        </Flex>
    );
};
